import "./carousel-item";
import { customElement, property, state } from "lit/decorators.js";
import { css, html, TemplateResult } from "lit";
import { LitElement } from "lit";
import { getCustomPropertyValueInPx } from "../../util/getCustomPropertyValueInPx";

@customElement("ehm-carousel")
export class Carousel extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: relative;
    }
    .track {
      overflow-x: auto;
      scrollbar-width: none;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      padding: 32px 0;
      margin-top: -32px;
      scroll-padding-left: 0;
    }
    .track.dragging {
      scroll-snap-type: none;
      scroll-behavior: auto;
      user-select: none;
    }
    .track::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }
    .inner {
      display: flex;
    }
    .indicator {
      max-width: 175px;
      width: 100%;
      height: 5px;
      border-radius: 2.5px;
      position: relative;
      background: var(--theme-border);
    }
    .indicator__inner {
      width: 50%;
      height: 5px;
      border-radius: 2.5px;
      position: absolute;
      top: 0;
      background: var(--button-primary-bg);
    }
    :host(:not([navigation])) .navigation {
      display: none;
    }
    .navigation {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1.5rem;
      & button {
        appearance: none;
        border: none;
        background: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 100px;
        width: 36px;
        height: 36px;
        background: var(--button-primary-bg);
        transition: background 150ms ease-in-out;
        --icon-color: var(--button-primary-fg);
        &:hover {
          background: var(--button-primary-bg_hover);
        }
        &:focus-visible {
          outline: var(--outline);
          outline-offset: var(--outline-offset);
        }
        &[disabled] {
          background: var(--theme-border);
          cursor: not-allowed;
        }
      }
    }
    @media (max-width: 330px) {
      .indicator {
        display: none;
      }
    }
  `;

  #items: NodeListOf<HTMLElement>;
  #gap = 32;
  #previousLabel = "Previous";
  #nextLabel = "Next";
  #indicator?: HTMLElement | undefined | null;
  #indicatorInner?: HTMLElement | undefined | null;
  #track?: HTMLElement | undefined | null;
  #trackScrollWidth = 0;
  #trackClientWidth = 0;
  #trackMaxScroll = 0;
  #animationFrameTimestamp: number | null = null;
  #isDragging = false;
  #startX = 0;
  #scrollLeft = 0;
  #hasDragged = false;
  #isButtonScrolling = false;
  #resizeObserver?: ResizeObserver;
  #resizeHandler = () => this.#decideMode();

  @state() private leftDisabled = true;
  @state() private rightDisabled = false;
  @state() private navigation = true;
  @state() private offset = 0;
  @state() private itemWidth = 0;
  @state() private indicatorWidth = 0;
  @property({ type: Boolean, reflect: true, attribute: "disabled" }) disabled = true;

  constructor() {
    super();
    this.classList.remove("grid");
    this.classList.remove("wrapper");
    this.classList.remove("wrapper--lg");
    this.#items = this.querySelectorAll("ehm-carousel-item");
  }

  firstUpdated() {
    this.#decideMode();
    this.#setupKeyboardNavigation();
    window.addEventListener("resize", this.#resizeHandler);
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("disabled") && !this.disabled) {
      this.#measure();
      this.#setupDragHandlers();
      this.#setupResizeObserver();
    }
    if (changedProperties.has("disabled")) {
      this.dispatchEvent(new Event("carousel-updated"));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    window.removeEventListener("resize", this.#resizeHandler);
  }

  #decideMode() {
    const parentWidth = this.#getParentWidth();
    const visibleCount = this.#getVisibleCount(parentWidth);

    if (this.#items.length <= visibleCount) {
      this.classList.add("grid");
      this.classList.add("wrapper");
      this.classList.add("wrapper--lg");
      this.disabled = true;
    } else {
      this.classList.remove("grid");
      this.classList.remove("wrapper");
      this.classList.remove("wrapper--lg");

      this.#gap = getCustomPropertyValueInPx("--gutter-lg");
      this.itemWidth = (parentWidth - this.#gap * (visibleCount - 1)) / visibleCount;
      this.#items.forEach((item) => {
        item.style.minWidth = this.itemWidth + "px";
      });

      const wasDisabled = this.disabled;
      this.disabled = false;

      // If already in carousel mode (no disabled toggle), measure directly
      // since updated() won't fire
      if (!wasDisabled) {
        this.#measure();
      }
    }

    this.offset = 0;
    this.leftDisabled = true;
    this.rightDisabled = false;
  }

  #measure() {
    this.#track = this.shadowRoot?.querySelector(".track");
    this.#indicator = this.shadowRoot?.querySelector(".indicator");
    this.#indicatorInner = this.shadowRoot?.querySelector(".indicator__inner");

    if (!this.#track) return;

    // Set scroll-padding to align snap points with the wrapper content edge
    const sitePadding = getCustomPropertyValueInPx("--site-padding");
    const siteMaxWidth = getCustomPropertyValueInPx("--site-max-width--lg");
    let edgePadding: number;
    if (window.innerWidth <= siteMaxWidth) {
      edgePadding = sitePadding;
    } else {
      edgePadding = (window.innerWidth - siteMaxWidth) / 2 + sitePadding;
    }
    this.#track.style.scrollPaddingLeft = `${Math.max(0, edgePadding - this.#gap)}px`;

    this.#trackScrollWidth = this.#track.scrollWidth;
    this.#trackClientWidth = this.#track.clientWidth;
    this.#trackMaxScroll = this.#trackScrollWidth - this.#trackClientWidth;

    if (this.#trackScrollWidth > 0) {
      this.indicatorWidth = (this.#trackClientWidth / this.#trackScrollWidth) * 100;
    }
    this.navigation = this.#trackMaxScroll > 1;
  }

  #setupResizeObserver() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
    });
    if (this.#track) {
      this.#resizeObserver.observe(this.#track);
    }
  }

  #getParentWidth(): number {
    const siteMaxWidth = getCustomPropertyValueInPx("--site-max-width--lg");
    const sitePadding = getCustomPropertyValueInPx("--site-padding");
    if (window.innerWidth < siteMaxWidth) return window.innerWidth - sitePadding * 2;
    return siteMaxWidth - sitePadding * 2;
  }

  #handleScroll(e: Event) {
    if (!this.#indicator) return;

    const target = e.target as HTMLElement;
    this.offset = target.scrollLeft;

    if (this.#animationFrameTimestamp) return;

    this.#animationFrameTimestamp = requestAnimationFrame(() => {
      // Only update button states if not button scrolling
      if (!this.#isButtonScrolling) {
        this.leftDisabled = !this.#canScrollLeft();
        this.rightDisabled = !this.#canScrollRight();
      }
      this.#indicatorInner!.style.left = `${(this.offset / this.#trackScrollWidth) * 100}%`;
      this.#animationFrameTimestamp = null;
    });
  }

  #getVisibleCount(availableWidth: number) {
    if (availableWidth <= 360)
      return 1.1; // Hint next item on screen
    else if (availableWidth < 500) return 1.1;
    else if (availableWidth < 768) return 2;
    else return 3;
  }

  #move(direction: "left" | "right") {
    let move = 0;
    if (direction === "left" && this.offset > 0) {
      move = -(this.itemWidth + this.#gap);
    } else if (direction === "right") {
      move = this.itemWidth + this.#gap;
    }

    const targetScroll = this.offset + move;

    // Set flag to prevent handleScroll from updating button states
    this.#isButtonScrolling = true;

    // Update button states immediately based on target position
    this.leftDisabled = targetScroll <= 1;
    this.rightDisabled = targetScroll >= this.#trackMaxScroll - 1;

    this.#track?.scroll({
      top: 0,
      left: targetScroll,
      behavior: "smooth"
    });

    // Reset flag after scroll completes
    setTimeout(() => {
      this.#isButtonScrolling = false;
    }, 600);
  }

  #canScrollLeft(): boolean {
    return this.offset > 1;
  }

  #canScrollRight(): boolean {
    return this.offset < this.#trackMaxScroll - 1;
  }

  #snapToNearest() {
    if (!this.#track) return;

    const scrollLeft = this.#track.scrollLeft;
    const itemWidthWithGap = this.itemWidth + this.#gap;
    const nearestIndex = Math.round(scrollLeft / itemWidthWithGap);
    const targetScroll = nearestIndex * itemWidthWithGap;

    this.#track.scroll({
      left: targetScroll,
      behavior: "smooth"
    });
  }

  #setupDragHandlers() {
    if (!this.#track) return;

    // Prevent default drag behavior on images and links
    const preventDefaultDrag = (e: Event) => {
      e.preventDefault();
    };

    this.#track.addEventListener("mousedown", (e: MouseEvent) => {
      // Ignore if mousedown was triggered by touch
      if ((e as any).sourceCapabilities?.firesTouchEvents) return;

      this.#isDragging = true;
      this.#hasDragged = false;
      this.#startX = e.pageX - this.#track!.offsetLeft;
      this.#scrollLeft = this.#track!.scrollLeft;
      this.#track!.classList.add("dragging");
    });

    this.#track.addEventListener("mouseleave", () => {
      if (this.#isDragging) {
        this.#isDragging = false;
        this.#track!.classList.remove("dragging");

        // Snap to nearest item after drag ends
        if (this.#hasDragged) {
          this.#snapToNearest();
        }
      }
    });

    this.#track.addEventListener("mouseup", () => {
      if (this.#isDragging) {
        this.#isDragging = false;
        this.#track!.classList.remove("dragging");

        // Snap to nearest item after drag ends
        if (this.#hasDragged) {
          this.#snapToNearest();
        }
      }
    });

    this.#track.addEventListener("mousemove", (e: MouseEvent) => {
      if (!this.#isDragging) return;
      e.preventDefault();

      const x = e.pageX - this.#track!.offsetLeft;
      const walk = x - this.#startX;

      if (Math.abs(walk) > 5) {
        this.#hasDragged = true;
      }

      this.#track!.scrollLeft = this.#scrollLeft - walk;
    });

    // Prevent click events on links and other elements when dragging
    this.#track.addEventListener("click", (e: Event) => {
      if (this.#hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        this.#hasDragged = false;
      }
    }, true);

    // Prevent default drag behavior on all child elements
    this.#track.addEventListener("dragstart", preventDefaultDrag);

    // Additional prevention for images
    const images = this.querySelectorAll("img");
    images.forEach(img => {
      img.addEventListener("dragstart", preventDefaultDrag);
      img.style.pointerEvents = "none";
    });

    // Additional prevention for links
    const links = this.querySelectorAll("a");
    links.forEach(link => {
      link.addEventListener("dragstart", preventDefaultDrag);
    });
  }

  #setupKeyboardNavigation() {
    this.#items.forEach((item) => {
      const focusableElement = item.querySelector("a, button, [tabindex]:not([tabindex=\"-1\"])") as HTMLElement;
      if (focusableElement) {
        focusableElement.addEventListener("focus", () => {
          if (!this.#track) return;

          const itemLeft = item.offsetLeft;
          const itemRight = itemLeft + item.offsetWidth;
          const scrollLeft = this.#track.scrollLeft;
          const scrollRight = scrollLeft + this.#track.clientWidth;

          // Scroll only if item is not fully visible
          if (itemLeft < scrollLeft || itemRight > scrollRight) {
            this.#track.scroll({
              left: itemLeft - this.#gap,
              behavior: "smooth"
            });
          }
        });
      }
    });
  }

  render(): TemplateResult {
    if (this.disabled) return html`
      <slot></slot>`;
    return html`
      <div class="track" @scroll="${this.#handleScroll}">
        <div class="inner">
          <slot></slot>
        </div>
      </div>
      ${this.navigation
        ? html`
          <div class="navigation">
            <button
              class="prev"
              aria-label="${this.#previousLabel}"
              ?disabled="${this.leftDisabled}"
              @click="${() => this.#move("left")}"
            >
              <ehm-icon name="caret" style="--icon-rotate: 180deg;"></ehm-icon>
            </button>
            <button
              class="next"
              ?disabled="${this.rightDisabled}"
              @click="${() => this.#move("right")}"
              aria-label="${this.#nextLabel}"
            >
              <ehm-icon name="caret"></ehm-icon>
            </button>
            <div class="indicator">
              <div class="indicator__inner" style="width: ${this.indicatorWidth}%"></div>
            </div>
          </div>`
        : html``}
    `;
  }
}
